import type { CatalogEntry } from "./types";

function parseHost(query: string): string | null {
  const lowered = query.trim().toLowerCase();
  const rest = lowered.startsWith("https://")
    ? lowered.slice(8)
    : lowered.startsWith("http://")
      ? lowered.slice(7)
      : lowered.startsWith("www.")
        ? lowered
        : null;
  if (!rest) return null;
  return rest.split("/")[0]?.split(":")[0] ?? null;
}

/** Keystroke search over the compact index; no network round trip. */
export function searchCatalog(entries: CatalogEntry[], query: string, limit = 50): CatalogEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [...entries].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
  }
  const host = parseHost(q);
  const scored = entries
    .map((entry) => {
      let score = 0;
      if (entry.display_name.toLowerCase().startsWith(q)) score += 100;
      if (entry.aliases.some((alias) => alias.toLowerCase().startsWith(q))) score += 80;
      if (entry.category.toLowerCase().includes(q)) score += 30;
      const haystack = [
        entry.entry_id,
        entry.display_name,
        entry.category,
        ...entry.aliases,
        ...entry.match_domains,
        ...entry.search_keywords,
      ]
        .join(" ")
        .toLowerCase();
      if (haystack.includes(q)) score += 20;
      if (
        host &&
        entry.match_domains.some((domain) => host === domain || host.endsWith(`.${domain}`))
      ) {
        score += 200;
      }
      return { entry, score: score + entry.popularity };
    })
    .filter(({ score }) => score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(({ entry }) => entry);
}

/** Bubble state machine, mirroring the server-computed `blocks` view. */
export function bubbleFor(
  entry: CatalogEntry,
  status: string,
  stage?: string,
  missingSecretForUser = false,
): { state: string; blocksRun: boolean; reason?: string } {
  if (status === "configured") {
    return missingSecretForUser
      ? {
          state: "grey",
          blocksRun: true,
          reason: "A secret is configured by another member; you cannot decrypt it",
        }
      : { state: "green", blocksRun: false };
  }
  if (status === "needs_attention") {
    return { state: "red", blocksRun: true, reason: "Session expired or credential rejected" };
  }
  if (status === "availability_review" || status === "provisioning") {
    return { state: "amber", blocksRun: true, reason: `Requested — ${stage ?? status}` };
  }
  if (
    status === "declined" ||
    status === "unavailable" ||
    !entry.supported ||
    entry.availability === "unavailable"
  ) {
    return { state: "grey_muted", blocksRun: true, reason: "Unavailable or declined" };
  }
  return { state: "grey", blocksRun: true };
}
